/**
 * Step 1: 소스 선택
 *
 * - 지식베이스 이름·설명 입력
 * - 파일 / 웹 / DB 데이터 소스 선택 (복수 선택 가능)
 * - isUserMode: 파일 카드만 표시, 체크박스 비활성
 * - lockedSource: 단일 소스 페이지에서 지정된 소스 카드만 표시, 체크박스 비활성
 * - lockedSource 없음(=복합 소스 페이지): 모든 카드 표시, 자유 선택
 */

import React, { useState, useEffect } from 'react';
import { isAdminRole } from '../../../utils/role';
import { SOURCE_LABELS, SourceType } from '../shared/pipeline.types';
import { authApi, customerApi, Customer } from '../../../api';
import { teamApi, Team } from '../../../api/teams';
import { embeddingProviderApi } from '../../../api/embeddingProvider';
import { Badge, Checkbox, Select } from '../../ui';
import userFileIllust from '../../../assets/illustrations/user-file-step-01.svg';
import userWebIllust from '../../../assets/illustrations/user-web-step-01.svg';
import userDbIllust from '../../../assets/illustrations/user-connector-step-01.svg';
import adminFileIllust from '../../../assets/illustrations/admin-file-step-01.svg';
import adminWebIllust from '../../../assets/illustrations/admin-web-step-01.svg';
import adminDbIllust from '../../../assets/illustrations/admin-connector-step-01.svg';

// 소스 카드 일러스트 - 각 소스의 1단계(수집) 일러스트, 역할별 테마 색
const SOURCE_ILLUSTS: Record<'user' | 'admin', Record<SourceType, string>> = {
  user: { file: userFileIllust, web: userWebIllust, database: userDbIllust },
  admin: { file: adminFileIllust, web: adminWebIllust, database: adminDbIllust },
};

interface SourceSelectStepProps {
  kbName: string;
  onKbNameChange: (v: string) => void;
  kbDescription: string;
  onKbDescriptionChange: (v: string) => void;
  /** 선택된 고객사 ID (상위 관리자 전용) */
  selectedCustomerId?: string;
  onCustomerIdChange?: (v: string) => void;
  /** 선택된 팀 ID (상위 관리자/고객사 관리자 전용) */
  selectedTeamId?: string;
  onTeamIdChange?: (v: string) => void;

  hasFile: boolean;
  onHasFileChange: (v: boolean) => void;
  hasWeb: boolean;
  onHasWebChange: (v: boolean) => void;
  hasDatabase: boolean;
  onHasDatabaseChange: (v: boolean) => void;

  activeSources: SourceType[];
  isUserMode: boolean;
  lockedSource?: SourceType;
}

export function SourceSelectStep({
  kbName, onKbNameChange,
  kbDescription, onKbDescriptionChange,
  selectedCustomerId, onCustomerIdChange,
  selectedTeamId, onTeamIdChange,
  hasFile, onHasFileChange,
  hasWeb, onHasWebChange,
  hasDatabase, onHasDatabaseChange,
  activeSources,
  isUserMode,
  lockedSource,
}: SourceSelectStepProps) {
  const currentUser = authApi.getUser();
  const role = currentUser?.role || '';
  const needsCustomerSelect = ['productAdmin', 'tenantAdmin', 'admin'].includes(role);
  const needsTeamSelect = ['productAdmin', 'tenantAdmin', 'customerAdmin', 'admin'].includes(role);

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    if (needsCustomerSelect) {
      customerApi.list().then(setCustomers).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const cId = needsCustomerSelect ? selectedCustomerId : currentUser?.customerId;
    if (needsTeamSelect && cId) {
      teamApi.listByCustomer(cId).catch(() => [] as Team[]).then(async (allTeams) => {
        // 각 팀의 임베딩 계정 확인 (team: 스코프로 직접 조회)
        const checks = await Promise.all(
          allTeams.map(t => embeddingProviderApi.listAccounts(`team:${t.id}`).catch(() => []))
        );
        setTeams(allTeams.filter((_, i) => checks[i].length > 0));
      });
    } else {
      setTeams([]);
    }
  }, [selectedCustomerId]);
  // 각 카드 표시 여부
  const showFile     = isUserMode || !lockedSource || lockedSource === 'file';
  const showWeb      = !isUserMode && (!lockedSource || lockedSource === 'web');
  const showDatabase = !isUserMode && (!lockedSource || lockedSource === 'database');

  // 체크박스 비활성 여부 (단일 소스 페이지·사용자 모드에서 변경 불가)
  const fileDisabled = isUserMode || lockedSource === 'file';
  const webDisabled  = !!lockedSource && lockedSource === 'web';
  const dbDisabled   = !!lockedSource && lockedSource === 'database';

  // 로그인 역할 기준 일러스트 (isUserMode 는 포탈 화면 구분용이라 별개)
  const illusts = SOURCE_ILLUSTS[isAdminRole(authApi.getUser()?.role) ? 'admin' : 'user'];

  const descText = isUserMode
    ? '파일을 업로드하여 지식베이스를 구축합니다.'
    : lockedSource
    ? `${SOURCE_LABELS[lockedSource]} 소스로 지식베이스를 구축합니다.`
    : '하나 이상 선택하세요. 다음 단계에서 각 소스별로 설정합니다.';

  return (
    <div className="form-grid">
      {/* 이름 */}
      <div className="form-item--md">
        <div className="form-label">
          지식베이스 이름 <span className="required">*</span>
        </div>
        <input
          className="pipe-settings-input"
          placeholder="예: 제품 매뉴얼, 기술 블로그"
          value={kbName}
          onChange={(e) => onKbNameChange(e.target.value)}
        />
      </div>

      {/* 설명 */}
      <div className="form-item--md">
        <div className="form-label">
          설명 <span className="optional">(선택)</span>
        </div>
        <input
          className="pipe-settings-input"
          placeholder="예: 회사 공식 문서 모음"
          value={kbDescription}
          onChange={(e) => onKbDescriptionChange(e.target.value)}
        />
      </div>

      {/* 고객사 선택 (productAdmin/tenantAdmin) */}
      {needsCustomerSelect && (
        <div className="form-item--md">
          <div className="form-label">고객사</div>
          <Select
            value={selectedCustomerId || ''}
            onChange={(e) => { onCustomerIdChange?.(e.target.value); onTeamIdChange?.(''); }}
          >
            <option value="">고객사 선택 (선택)</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </div>
      )}

      {/* 팀 선택 (productAdmin/tenantAdmin/customerAdmin) */}
      {needsTeamSelect && (
        <div className="form-item--md">
          <div className="form-label">팀 <span className="required">*</span></div>
          <Select
            value={selectedTeamId || ''}
            onChange={(e) => onTeamIdChange?.(e.target.value)}
            disabled={needsCustomerSelect && !selectedCustomerId}
          >
            <option value="">
              {needsCustomerSelect && !selectedCustomerId ? '고객사를 먼저 선택하세요' : '팀 선택'}
            </option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
        </div>
      )}

      {/* 소스 선택 - 라벨 + 설명 (카드는 아래 다음 줄) */}
      <div className="form-item--md">
        <div className="form-label">
          데이터 소스 <span className="required">*</span>
          <div className="optional">{descText}</div>
        </div>
        <div className="pipe-source-select-cards">
          {/* 파일 카드 */}
          {showFile && (
            <label
              className={`pipe-source-select-card${hasFile ? ' active' : ''}${fileDisabled ? ' single' : ''}`}
            >
              <Checkbox
                checked={hasFile}
                onChange={(e) => onHasFileChange(e.target.checked)}
                disabled={fileDisabled}
              />
              <img className="pipeline-step-illust" src={illusts.file} alt="" />
              <div className='pipe-source-select-content'>
                <div className="pipe-source-select-label">파일 업로드</div>
                <div className="pipe-source-select-sub">PDF · TXT · Excel · 이미지</div>
              </div>
            </label>
          )}

          {/* 웹 카드 */}
          {showWeb && (
            <label
              className={`pipe-source-select-card${hasWeb ? ' active' : ''}${webDisabled ? ' single' : ''}`}
            >
              <Checkbox
                checked={hasWeb}
                onChange={(e) => onHasWebChange(e.target.checked)}
                disabled={webDisabled}
              />
              <img className="pipeline-step-illust" src={illusts.web} alt="" />
              <div className='pipe-source-select-content'>
                <div className="pipe-source-select-label">웹 크롤링</div>
                <div className="pipe-source-select-sub">URL · Sitemap · 블로그</div>
              </div>
            </label>
          )}

          {/* DB 카드 */}
          {showDatabase && (
            <label
              className={`pipe-source-select-card${hasDatabase ? ' active' : ''}${dbDisabled ? ' single' : ''}`}
            >
              <Checkbox
                checked={hasDatabase}
                onChange={(e) => onHasDatabaseChange(e.target.checked)}
                disabled={dbDisabled}
              />
              <img className="pipeline-step-illust" src={illusts.database} alt="" />
              <div className='pipe-source-select-content'>
                <div className="pipe-source-select-label">DB 커넥터</div>
                <div className="pipe-source-select-sub">MySQL · PostgreSQL · Oracle</div>
              </div>
            </label>
          )}
        </div>
        {/* 선택 요약 — 복합 소스 모드에서만 표시 */}
        {activeSources.length > 0 && !isUserMode && !lockedSource && (
          <div className="pipe-source-summary">
            <span>선택된 소스 : </span>
            {activeSources.map((s) => (
              <Badge key={s} variant={`source-${s}`}>{SOURCE_LABELS[s]}</Badge>
            ))}
            <span> 다음 단계에서 각 소스를 순서대로 설정합니다.</span>
          </div>
        )}
      </div>


    </div>
  );
}
